import React, {useState, useEffect} from "react";
import {useLocation, useNavigate} from "react-router-dom";
import {useGlobalContext} from "../context/GlobalState";
import {UserTab} from "./UserTab";
import {getCurrentTime} from "../services/Utils";
import QsiIcon from "../images/QSI-Logo-Icon.png";
import packageJson from "../../package.json";
import dayjs from "dayjs";
import TimeAgo from 'react-timeago'
// MUI Imports
import {styled, useTheme} from "@mui/material/styles";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import LocalAtmOutlined from "@mui/icons-material/LocalAtmOutlined";
import InsertInvitationIcon from "@mui/icons-material/InsertInvitation";
import RefreshIcon from "@mui/icons-material/Refresh";
import GroupsIcon from '@mui/icons-material/Groups';
import AdminPanelSettingsOutlinedIcon from '@mui/icons-material/AdminPanelSettingsOutlined';
import {EqualizerOutlined} from "@mui/icons-material";
import MuiDrawer from "@mui/material/Drawer";
import {
    Box,
    List,
    Typography,
    Divider,
    IconButton,
    ListItem,
    ListItemIcon,
    ListItemText,
    ListItemButton,
    Tooltip, 
    Switch, 
    FormControlLabel
} from "@mui/material";
// MUI Date Range Picker Imports
import {DesktopDateRangePicker} from "@mui/x-date-pickers-pro";
import { LocalizationProvider } from '@mui/x-date-pickers-pro/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers-pro/AdapterDayjs';

const styles = {
    datePickerBox: {
        display: "flex",
        flexDirection: "column",
        textAlign: "center",
        marginTop: 2,
        gap: 1.5,
        px: 1,
        mb: 3,
    },
    footerText: {
        fontSize: 10,
        lineHeight: 1.2,
    },
};

const drawerWidth = 220;

/**
 * openMixin - Controls drawer width when open
 * @param {object} theme - The theme object
 * @returns
 */
const openedMixin = (theme) => ({
    width: drawerWidth,
    transition: theme.transitions.create("width", {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.enteringScreen,
    }),
    overflowX: "hidden",
});

/**
 * closedMixin - Controls drawer width when closed
 * @param {object} theme - The theme object
 * @returns
 */
const closedMixin = (theme) => ({
    transition: theme.transitions.create("width", {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.leavingScreen,
    }),
    overflowX: "hidden",
    width: `calc(${theme.spacing(7)} + 1px)`,
});

/**
 * DrawerHeader - Controls styling of drawer header
 * @param {object} theme - The theme object
 * @returns
 */
const DrawerHeader = styled("div")(({theme}) => ({
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    padding: theme.spacing(1, 1),
    height: "48px"
}));

/**
 * Drawer - Controls styling of the drawer
 * @param {object} theme - The theme object
 * @returns
 */
const Drawer = styled(MuiDrawer, {
    shouldForwardProp: (prop) => prop !== "open",
})(({open}) => {
    const theme = useTheme();
    return {
        width: drawerWidth,
        flexShrink: 0,
        whiteSpace: "nowrap",
        boxSizing: "border-box",
        ...(open && {
            ...openedMixin(theme),
            "& .MuiDrawer-paper": {
                ...openedMixin(theme),
                backgroundColor: theme.palette.primary.componentbg,
            },
        }),
        ...(!open && {
            ...closedMixin(theme),
            "& .MuiDrawer-paper": {
                ...closedMixin(theme),
                backgroundColor: theme.palette.primary.componentbg,
            },
        }),
    };
});

const ThemeSwitch = styled(Switch)(({theme}) => ({
    width: 62,
    height: 34,
    padding: 7,
    '& .MuiSwitch-switchBase': {
        margin: 1,
        padding: 0,
        transform: 'translateX(6px)', '&.Mui-checked': {
            color: '#fff',
            transform: 'translateX(22px)',
            '& .MuiSwitch-thumb:before': {
                backgroundImage: `url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" height="20" width="20" viewBox="0 0 20 20"><path fill="${encodeURIComponent('#fff',)}" d="M4.2 2.5l-.7 1.8-1.8.7 1.8.7.7 1.8.6-1.8L6.7 5l-1.9-.7-.6-1.8zm15 8.3a6.7 6.7 0 11-6.6-6.6 5.8 5.8 0 006.6 6.6z"/></svg>')`,
            }, '& + .MuiSwitch-track': {
                opacity: 1,
                backgroundColor: theme.palette.gray.dark,
            },
        },
    }, '& .MuiSwitch-thumb': {
        backgroundColor: theme.palette.primary.main,
        width: 32,
        height: 32,
        '&::before': {
            content: "''",
            position: 'absolute',
            width: '100%',
            height: '100%',
            left: 0,
            top: 0,
            backgroundRepeat: 'no-repeat',
            backgroundPosition: 'center',
            backgroundImage: `url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" height="20" width="20" viewBox="0 0 20 20"><path fill="${encodeURIComponent('#fff',)}" d="M9.305 1.667V3.75h1.389V1.667h-1.39zm-4.707 1.95l-.982.982L5.09 6.072l.982-.982-1.473-1.473zm10.802 0L13.927 5.09l.982.982 1.473-1.473-.982-.982zM10 5.139a4.872 4.872 0 00-4.862 4.86A4.872 4.872 0 0010 14.862 4.872 4.872 0 0014.86 10 4.872 4.872 0 0010 5.139zm0 1.389A3.462 3.462 0 0113.471 10a3.462 3.462 0 01-3.473 3.472A3.462 3.462 0 016.527 10 3.462 3.462 0 0110 6.528zM1.665 9.305v1.39h2.083v-1.39H1.666zm14.583 0v1.39h2.084v-1.39h-2.084zM5.09 13.928L3.616 15.4l.982.982 1.473-1.473-.982-.982zm9.82 0l-.982.982 1.473 1.473.982-.982-1.473-1.473zM9.305 16.25v2.083h1.389V16.25h-1.39z"/></svg>')`,
        },
    }, '& .MuiSwitch-track': {
        opacity: 1,
        backgroundColor: theme.palette.gray.light,
        borderRadius: 20 / 2,
    },
}));

/**
 * Controls navigation items in the drawer
 * @type {array} - Title, url, and icon of the navigation items
 * @returns
 */
const navItems = [
  {
    title: "Dashboard",
    url: "/",
    icon: EqualizerOutlined,
    roles: ["admin", "reviewer", "viewer"]
  },
  {
    title: "Deposit Review",
    url: "/deposit-review",
    icon: LocalAtmOutlined,
    roles: ["admin", "reviewer", "viewer"],
  },
  {
    title: "Manage Users",
    url: "/manage-users",
    icon: GroupsIcon,
    roles: ["admin"],
  },
  {
    title: "Admin Panel",
    url: "/admin-panel",
    icon: AdminPanelSettingsOutlinedIcon,
    roles: ["admin"]
  }
];

export function SideNav() {
    const theme = useTheme();
    const [open, setOpen] = useState(true);
    const [active, setActive] = useState();
    const [pending, setPending] = useState();
    const [currentTime, setCurrentTime] = useState(getCurrentTime());
    const [rotation, setRotation] = useState(0);
    const minSelectableDate = dayjs().subtract(6, "months");
    const {
        themeMode,
        setThemeMode,
        user,
        checkData,
        startDate,
        setStartDate,
        endDate,
        setEndDate,
        updatedDataTime,
        getCheckData,
    } = useGlobalContext();

    // Get current path react-router-dom
    const location = useLocation();
    const navigate = useNavigate();

    // Set active path
    useEffect(() => {
        let path = navItems.find((item) => item.url === location.pathname);
        setActive(path?.title);
    }, [location.pathname]);

    // Set pending reviews
    useEffect(() => {
        const allPending = checkData?.filter(
            (review) => review.reviewState === "Pending"
        );
        setPending(allPending?.length || 0);
    }, [checkData]);

    // Set current time
    useEffect(() => {
        const interval = setInterval(() => {
            setCurrentTime(getCurrentTime());
        }, 60000);
        return () => clearInterval(interval);
    }, []);

    const setNewStartDate = (newStartDate) => {
        // newStartDate is a valid date
        if (!dayjs(newStartDate).isValid()) {
            setStartDate(dayjs().startOf("day"));
        }

        // newStartDate is not in the future
        if (newStartDate > dayjs()) {
            newStartDate = dayjs();
        }

        // newStartDate is not more than 12 months in the past
        if (newStartDate < minSelectableDate) {
            newStartDate = minSelectableDate;
        }

        // newStartDate does not exceed endDate
        if (newStartDate > endDate) {
            setEndDate(newStartDate);
        }

        setStartDate(newStartDate.startOf("day"));
    };

    const setNewEndDate = (newEndDate) => {
        // newEndDate is a valid date
        if (!dayjs(newEndDate).isValid()) {
            setEndDate(dayjs().endOf("day"));
        }

        // newEndDate is not in the future
        if (newEndDate > dayjs()) {
            newEndDate = dayjs();
        }

        // newEndDate is not more than 12 months in the past
        if (newEndDate < minSelectableDate) {
            newEndDate = minSelectableDate;
        }

        // newEndDate is not before startDate
        if (newEndDate < startDate) {
            setStartDate(newEndDate);
        }

        setEndDate(newEndDate.endOf("day"));
    };

    function handleThemeMode() {
        let mode = themeMode === 'dark' ? 'light' : 'dark'
        localStorage.setItem('themeMode', mode)
        setThemeMode(mode)
    }

    /**
     * Controls side navigation drawer open and close
     * @returns
     */
    const handleDrawer = () => {
        setOpen(open ? false : true);
    };

    return (
        <>
            <Drawer variant="permanent" open={open}>
                <DrawerHeader sx={{justifyContent: "center", alignSelf: "center"}}>
                    {open ?
                        <Typography variant="h5" sx={{
                            color: "primary.black",
                        }}><b>QSI</b>BANKING</Typography>
                        :
                        <img key={"drawer_logo"} src={QsiIcon} alt="QSI Logo"
                             style={{height: '40px', borderRadius: '4px'}}/>
                    }
                </DrawerHeader>
                <Divider/>

                {/* Holds user tab that provides user profile, account, and logout */}
                <DrawerHeader sx={{justifyContent: "flex-start"}}>
                    <UserTab open={open}/>
                </DrawerHeader>

                <Divider/>
                <List>
                    {navItems
                        .filter(item => item.roles.map(role => role.toLowerCase()).includes(user.role.toLowerCase()))
                        .map((item) => (
                            <ListItem disablePadding sx={{display: "block"}} key={item.title}>
                                <ListItemButton
                                    title= {open ? "" : item.title}
                                    onClick={() => navigate(item.url)}
                                    sx={
                                        active === item.title
                                            ? {
                                                backgroundColor: "primary.main",
                                                color: "white",
                                                "&:hover": {
                                                    backgroundColor: "primary.main",
                                                    color: "white",
                                                    minHeight: 48,
                                                    justifyContent: open ? "initial" : "center",
                                                },
                                            }
                                            : {
                                                color: "primary.main",
                                                "&:hover": {
                                                    backgroundColor: "primary.main",
                                                    color: "white",
                                                    minHeight: 48,
                                                    justifyContent: open ? "initial" : "center",
                                                },
                                            }
                                    }
                                >
                                    <ListItemIcon
                                        sx={{
                                            minWidth: 0,
                                            mr: open ? 1 : "auto",
                                            justifyContent: "center",
                                            color: "inherit",
                                        }}
                                    >
                                        <item.icon/>
                                    </ListItemIcon>
                                    <ListItemText
                                        primary={item.title}
                                        sx={{opacity: open ? 1 : 0}}
                                    />
                                    {item.title === "Deposit Review" && open === true && (
                                        <Typography
                                            sx={{
                                                display: "flex",
                                                justifyContent: "center",
                                                alignItems: "center",
                                                backgroundColor: "primary.background",
                                                color: "primary.main",
                                                borderRadius: 1,
                                                borderColor: "primary.main",
                                                border: 1,
                                                height: "30px",
                                                width: "40px",
                                                m: "-6px",
                                            }}
                                        >
                                            {pending}
                                        </Typography>
                                    )}
                                </ListItemButton>
                            </ListItem>
                        ))}
                </List>

                <Divider/>

                {/* Global Deposit Date Range Search */}
                {open ? (
                    <Box sx={styles.datePickerBox}>
                        <Typography color="primary.main">Deposit Date Range</Typography>
                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                            <DesktopDateRangePicker
                                startText="Start"
                                endText="End"
                                value={[startDate, endDate]}
                                onChange={(newValue) => {
                                    const [newStartDate, newEndDate] = newValue;
                                    setNewStartDate(newStartDate);
                                    setNewEndDate(newEndDate);
                                }}
                                minDate={minSelectableDate}
                                maxDate={dayjs().add(1, "month")}
                                disableFuture
                                slotProps={{
                                    popper: {placement: 'right-start'}
                                }}
                                sx={{
                                    flexDirection: 'column',
                                    gap: 1,
                                    '& .MuiFormControl-root': {
                                        margin: 0
                                    },
                                    '& .MuiMultiInputDateRangeField-separator': {
                                        display: 'none'
                                    },
                                }}
                            />
                        </LocalizationProvider>
                    </Box>
                ) : (
                    <IconButton
                        title="Date Range"
                        onClick={handleDrawer}
                        sx={{
                            color: "primary.main",
                            borderRadius: 0,
                        }}
                    >
                        <InsertInvitationIcon/>
                    </IconButton>
                )}

                <Divider/>

                {/* Update Check Data Button and Text */}
                {open && (
                    <Box
                        sx={{
                            display: "flex",
                            flexDirection: "row",
                            alignItems: "center",
                            justifyContent: "center",
                            padding: 1,
                            textAlign: "center",
                            maxWidth: "inherit",
                        }}
                    >
                        <Typography sx={{fontSize: "14px", lineHeight: 1.2}}>
                            Deposit Data Last Updated
                            <br/>
                            <TimeAgo date={updatedDataTime} />
                            
                        </Typography>
                        <Tooltip title="Refresh Check Data" placement="bottom">
                            <IconButton
                                label="Refresh Check Data"
                                onClick={() => {
                                    getCheckData();
                                    setRotation(rotation + 360);
                                }}
                            >
                                <RefreshIcon
                                    sx={{
                                        transform: `rotate(${rotation}deg)`,
                                        transition: "transform 1.0s ease-in-out",
                                    }}
                                />
                            </IconButton>
                        </Tooltip>
                    </Box>
                )}

                {/* SideNav Footer Text */}
                <Box
                    sx={{
                        display: "flex",
                        flexDirection: "column",
                        position: "absolute",
                        bottom: open ? 0 : 30,
                        alignSelf: "center",
                        padding: 1,
                        textAlign: "center",
                        justifyContent: "center",
                    }}
                >
                    <FormControlLabel
                        control={<ThemeSwitch sx={{m: 1}} checked={themeMode === 'dark'}/>}
                        onChange={handleThemeMode}
                        label=""
                        aria-label="Toggle Theme"
                        style={{display: 'flex', justifyContent: 'center', margin: 0}}
                        title={themeMode === 'light' ? "Toggle Dark" : "Toggle Light"}
                    />
                    {open && (
                        <>
                            <Typography variant="caption" sx={styles.footerText}>
                                QSI Deposit Review v{packageJson.version}
                            </Typography>
                            <Typography variant="caption" sx={styles.footerText}>
                                Copyright ©️ 2023 QSI, Inc.
                            </Typography>
                        </>
                    )}
                </Box>

                {/* Open/Close Arrow Button */}
                <IconButton
                    title={open ? "Close Nav" : "Open Nav"}
                    onClick={handleDrawer}
                    sx={{
                        position: "absolute",
                        bottom: 0,
                        right: open ? 0 : 5,
                        color: "primary.main",
                    }}
                >
                    {open ? <ChevronLeftIcon/> : <ChevronRightIcon/>}
                </IconButton>
            </Drawer>
        </>
    );
}
